import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Mail, PackageCheck, Printer, TriangleAlert } from 'lucide-react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  useWarehouseOrderDetail,
  useReportIssue,
  useNotifyCustomer,
  useMarkReadyForShipping,
} from '@/hooks/useWarehouse';
import type { ShippingDocumentType } from '@/apis/warehouse';
import { useAuthStore } from '@/stores/authStore';
import { ROUTES } from '@/constants/routes';
import {
  resolveClassification,
  CLASSIFICATION_LABEL,
  CLASSIFICATION_STYLE,
  canActOnOrder,
  hasAllItemsFound,
} from '../warehouse-shared';

const REQUIRED_DOCUMENT_TYPES: ShippingDocumentType[] = ['invoice', 'label', 'bill_of_lading'];
import { PackingListSection } from './PackingListSection';
import { PackagesSection } from './PackagesSection';
import { NotesSection } from './NotesSection';
import { FilesSection } from './FilesSection';
import { TrackingSection } from './TrackingSection';
import { ShippingInfoSection } from './ShippingInfoSection';

interface Props {
  orderId: string | null;
  onClose: () => void;
}

export function OrderDetailDrawer({ orderId, onClose }: Props) {
  const open = !!orderId;
  const navigate = useNavigate();
  const { data, isLoading } = useWarehouseOrderDetail(orderId ?? '');
  const reportIssue = useReportIssue(orderId ?? '');
  const notifyCustomer = useNotifyCustomer(orderId ?? '');
  const markReadyForShipping = useMarkReadyForShipping(orderId ?? '');

  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [issueText, setIssueText] = useState('');
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [readyDialogOpen, setReadyDialogOpen] = useState(false);

  const order = data?.order;
  const packages = data?.packages ?? [];
  const classification = order ? resolveClassification(order.orderClassification, order.shippingAddress?.country) : null;
  const currentUser = useAuthStore((s) => s.user);
  const canAct = canActOnOrder(currentUser, order?.assignedSalespersonId, order?.assignedWarehouseHandlerId);

  const shippingDocuments = order?.metadata?.shippingDocuments;
  const allDocumentsUploaded = REQUIRED_DOCUMENT_TYPES.every((type) => !!shippingDocuments?.[type]);
  const canMarkReadyForShipping =
    canAct && packages.length > 0 && hasAllItemsFound(order?.items) && allDocumentsUploaded;
  const showReadyForShipping = order?.warehouseStatus === 'label_awaiting';

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent className="flex flex-col p-0">
        {isLoading || !order ? (
          <div className="flex flex-col gap-4 p-6">
            <Skeleton className="h-8 w-1/2" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-5">
              <div className="mb-5 flex flex-wrap items-center gap-2">
                {classification && (
                  <span className={`rounded px-2.5 py-1 text-xs font-semibold ${CLASSIFICATION_STYLE[classification]}`}>
                    {CLASSIFICATION_LABEL[classification]}
                  </span>
                )}
                <span className="rounded bg-smoke1 px-2.5 py-1 text-xs font-semibold text-black">
                  {order.shippingAddress?.country || 'US'}
                </span>
              </div>

              <button
                type="button"
                onClick={() => navigate({ to: ROUTES.WAREHOUSE_ORDER_DETAIL(order.id) })}
                className="mb-1 block text-2xl font-bold text-black hover:text-primary-s500"
              >
                Order #{order.orderNumber}
              </button>
              <p className="mb-4 text-sm font-normal text-lightGray">
                Customer: <span className="font-semibold text-black">{order.customer?.fullName}</span>
              </p>

              {!canAct && (
                <p className="mb-4 rounded-lg bg-smoke1 px-3 py-2 text-xs font-medium text-lightGray">
                  You're not the assigned Sales or Handler for this order — actions are read-only.
                </p>
              )}

              <div className="flex flex-col gap-6">
                <PackingListSection orderId={order.id} items={order.items ?? []} canAct={canAct} />
                <PackagesSection
                  orderId={order.id}
                  packages={packages}
                  shippingDocuments={shippingDocuments}
                  canAct={canAct}
                />
                <NotesSection order={order} canAct={canAct} />
                <FilesSection order={order} canAct={canAct} />
                <TrackingSection
                  orderId={order.id}
                  entries={data?.inboundTracking ?? []}
                  assignedSalespersonId={order.assignedSalespersonId}
                  assignedWarehouseHandlerId={order.assignedWarehouseHandlerId}
                  canAct={canAct}
                />
                <ShippingInfoSection
                  address={order.shippingAddress}
                  email={order.customer?.email}
                  customerName={order.customer?.fullName}
                />
              </div>
            </div>

            <div className="flex items-center gap-3 border-t border-smoke px-6 py-4">
              <button
                type="button"
                onClick={() => setIssueDialogOpen(true)}
                disabled={!canAct}
                className="flex items-center gap-1.5 rounded-full border border-danger px-4 py-2 text-sm font-medium text-danger transition-colors hover:bg-danger-light disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
              >
                <TriangleAlert size={14} />
                Report Issue
              </button>
              <button
                type="button"
                onClick={() => window.print()}
                className="flex items-center gap-1.5 rounded-full border border-primary-s500 px-4 py-2 text-sm font-medium text-primary-s500 transition-colors hover:bg-primary-s50"
              >
                <Printer size={14} />
                Print Check-List
              </button>
              <button
                type="button"
                onClick={() => {
                  setEmailSubject(`Update on your order #${order.orderNumber}`);
                  setEmailMessage('');
                  setEmailDialogOpen(true);
                }}
                disabled={!canAct}
                className="ml-auto flex items-center gap-1.5 rounded-full bg-primary-s500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-primary-s500"
              >
                <Mail size={14} />
                Send Email
              </button>
              {showReadyForShipping && (
                <button
                  type="button"
                  onClick={() => setReadyDialogOpen(true)}
                  disabled={!canMarkReadyForShipping}
                  title={
                    canMarkReadyForShipping
                      ? undefined
                      : 'Requires all items found, at least one package, and all 3 shipping documents uploaded'
                  }
                  className="flex items-center gap-1.5 rounded-full bg-success px-4 py-2 text-sm font-medium text-white transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <PackageCheck size={14} />
                  Ready for Shipping
                </button>
              )}
            </div>
          </>
        )}
      </SheetContent>

      <Dialog open={issueDialogOpen} onOpenChange={setIssueDialogOpen}>
        <DialogContent className="w-110">
          <DialogHeader className="border-b border-smoke pb-3">
            <DialogTitle>Report an Issue</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-2 py-3">
            <label className="text-xs font-normal text-lightGray">Describe the issue</label>
            <textarea
              value={issueText}
              onChange={(e) => setIssueText(e.target.value)}
              rows={4}
              placeholder="e.g. Part 890-2435 is out of stock, missing from warehouse shelf..."
              className="resize-none rounded-xl border border-smoke px-3 py-2 text-sm outline-none focus:border-primary-s500"
            />
            <p className="text-xs text-lightGray">This moves the order to the Issue column.</p>
          </div>
          <div className="mt-2 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setIssueDialogOpen(false)}
              className="rounded-full border border-smoke px-5 py-2 text-sm text-black hover:bg-smoke1"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={reportIssue.isPending || !issueText.trim()}
              onClick={() =>
                reportIssue.mutate(
                  { text: issueText.trim() },
                  { onSuccess: () => { setIssueText(''); setIssueDialogOpen(false); } }
                )
              }
              className="rounded-full bg-danger px-5 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              {reportIssue.isPending ? 'Reporting…' : 'Report Issue'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="w-110">
          <DialogHeader className="border-b border-smoke pb-3">
            <DialogTitle>Send Email to Customer</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3 py-3">
            <p className="text-sm font-normal text-lightGray">
              This sends an email to{' '}
              <span className="font-medium text-black">{order?.customer?.email || 'this customer'}</span>.
            </p>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-normal text-lightGray">Subject</label>
              <input
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                className="h-10 rounded-xl border border-smoke px-3 text-sm outline-none focus:border-primary-s500"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-normal text-lightGray">Message</label>
              <textarea
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                rows={4}
                placeholder="e.g. Your order has been packed and is ready for shipment..."
                className="resize-none rounded-xl border border-smoke px-3 py-2 text-sm outline-none focus:border-primary-s500"
              />
            </div>
          </div>
          <div className="mt-2 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setEmailDialogOpen(false)}
              className="rounded-full border border-smoke px-5 py-2 text-sm text-black hover:bg-smoke1"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={notifyCustomer.isPending || !emailSubject.trim() || !emailMessage.trim() || !order?.customer?.email}
              onClick={() =>
                notifyCustomer.mutate(
                  { subject: emailSubject.trim(), message: emailMessage.trim() },
                  { onSuccess: () => setEmailDialogOpen(false) }
                )
              }
              className="rounded-full bg-primary-s500 px-5 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {notifyCustomer.isPending ? 'Sending…' : 'Confirm & Send'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={readyDialogOpen} onOpenChange={setReadyDialogOpen}>
        <DialogContent className="w-100">
          <DialogHeader className="border-b border-smoke pb-3">
            <DialogTitle>Mark Ready for Shipping?</DialogTitle>
          </DialogHeader>
          <p className="py-3 text-sm font-normal text-lightGray">
            This moves order <span className="font-medium text-black">#{order?.orderNumber}</span> from{' '}
            <span className="font-medium text-black">Label Awaiting</span> to{' '}
            <span className="font-medium text-black">Shipment / Will Call</span>.
          </p>
          <div className="mt-2 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setReadyDialogOpen(false)}
              className="rounded-full border border-smoke px-5 py-2 text-sm text-black hover:bg-smoke1"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={markReadyForShipping.isPending}
              onClick={() =>
                markReadyForShipping.mutate(undefined, { onSettled: () => setReadyDialogOpen(false) })
              }
              className="rounded-full bg-success px-5 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              {markReadyForShipping.isPending ? 'Updating…' : 'Confirm'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </Sheet>
  );
}
